import { useSearchParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import { Box, Button, Fade, Slide, Typography, useTheme } from '@mui/material'

import limitsReachedImg from '../../../../shared/assets/limits-reached.svg'
import waitingSimba from '../../../../shared/assets/simba-ai/waiting.png'
import sadSimba from '../../../../shared/assets/simba-ai/sad.png'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import DataLoadingDots from '../../../../shared/components/common/DataLoadingDots'
import SubjectTopics from './view/SubjectTopics'
import LearnModeHome from './view/LearnModeHome'
import { useSettingsStore } from '../../../store/settings.store'
import AiResponse from './view/AiResponse'
import { TransitionGroup } from 'react-transition-group'
import { Icons } from '../../../../shared/utils/icons.jsx'

const getTrasitionsKey = ( view ) => {
    switch ( view ) {
        case "loading":
            return 6

        case "simba-ai-response-error":
            return 5

        case "limits-reached":
            return 4

        case "subject-learn-topics":
            return 3

        case "simba-ai-response":
        case "fetching-simba-ai-response":
        case "completed-typing-simba-ai-response":
            return 2

        case "home":
            return 1

        default:
            return 0
    }
}

const Renderer = observer( ( { closeAiMentorDialog } ) => {

    const SimbaAIStore = useSimbaAIStore()
    const SettingsStore = useSettingsStore()

    const { palette } = useTheme()

    const setSearchParams = useSearchParams().at( 1 )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const backToHome = async () => {
        SimbaAIStore.setView( "home", "right" )
    }


    const retryForAiResponse = async () => {
        if ( SimbaAIStore.getAiResponsesQueue.request ) {
            SimbaAIStore.addResponse( { topic: SimbaAIStore.getAiResponsesQueue.request, response: SimbaAIStore.getAiResponsesQueue.response } )
            SimbaAIStore.setAiResponsesQueue( { request: "", response: "" } )
        }
        try {
            SimbaAIStore.setView( 'fetching-simba-ai-response', "left", false )
            if ( await SimbaAIStore.getAIResponse( SimbaAIStore.getCurrentTopic ) )
                SimbaAIStore.setView( 'simba-ai-response', "left", false )
            else
                SimbaAIStore.setView( 'simba-ai-response-error', "left", false )

        } catch ( err ) {
            console.log( err )
        }
    }


    switch ( SimbaAIStore.getView.current ) {
        case "loading":
            return (
                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "10px"
                    }}>
                    <Box
                        component="img"
                        src={waitingSimba}
                        sx={{
                            width: "25vw",
                            borderRadius: "50%",
                            aspectRatio: "1 / 1",
                            objectFit: "cover"
                        }} />
                    <DataLoadingDots padding="20px" waitingMessage="Waiting for Simba AI" />
                </Box>
            );

        case "simba-ai-response-error":
            return (
                <Box sx={{
                    padding: "20px"
                }}>
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            border: `2px dashed ${palette.borderColor}`,
                            borderRadius: "20px",
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            padding: "20px",
                            gap: "10px",
                            justifyContent: "center"
                        }}>
                        <Box
                            sx={{
                                position: "absolute",
                                height: "60%",
                                maxHeight: "400px",
                                bgcolor: palette.contentBg,
                                borderRadius: "50%",
                                aspectRatio: "1 / 1",
                                zIndex: 1
                            }} />
                        <Box
                            component="img"
                            src={sadSimba}
                            alt="No practice sessions"
                            sx={{
                                position: "relative",
                                height: "180px",
                                zIndex: 10
                            }} />
                        <Typography
                            variant="subtitle2"
                            component="p"
                            sx={{
                                position: "relative",
                                textAlign: "center",
                                zIndex: 10
                            }}>{SimbaAIStore.getErrors.aiResponse}</Typography>
                        <Box
                            sx={{
                                display: "flex",
                                gap: "10px",
                                alignItems: "center"
                            }}>
                            <Button onClick={backToHome} sx={{ borderBottom: `4px solid ${palette.secondary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack} variant='outlined' color='secondary' >Back to Home</Button>
                            <Button className='animation-wave' onClick={retryForAiResponse} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Replay} variant='contained' disableElevation color='primary' >Retry</Button>
                        </Box>
                    </Box>
                </Box>
            );


        case "limits-reached":
            return (
                <Box sx={{
                    padding: "20px"
                }}>
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            gap: "20px",
                            alignItems: "center",
                            flexDirection: "column",
                            padding: "20px",
                            justifyContent: "center"
                        }}>
                        <Box component="img" alt='daily-limits-reached' src={sadSimba} sx={{
                            width: "200px"
                        }} />
                        <Typography variant='subtitle2'>You reached the limit for the day! You can only make upto {SettingsStore.getUseSettings['aitutor_max_requests_per_day']} requests per day.</Typography>
                        <Button variant='contained' color='secondary' sx={{ textTransform: "capitalize" }} disableElevation onClick={closeAiMentorDialog} >Okay</Button>
                    </Box></Box>
            );

        case "subject-learn-topics":
            return <SubjectTopics />

        case "simba-ai-response":
        case "fetching-simba-ai-response":
        case "completed-typing-simba-ai-response":
            return <AiResponse />

        case "home":
            return <LearnModeHome closeAiMentorDialog={closeAiMentorDialog} />

        default:
            return null
    }
} )

const LearnModeViewRenderer = observer( ( { closeAiMentorDialog } ) => {

    const SimbaAIStore = useSimbaAIStore()

    return (
        <Box
            sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                position: "relative",
                gap: "10px"
            }}>

            <TransitionGroup style={{ minHeight: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "auto" }}>
                <Fade
                    key={getTrasitionsKey( SimbaAIStore.getView.current )}
                    timeout={{ enter: 100, exit: 0 }}
                >
                    <Slide
                        in
                        direction={SimbaAIStore.getView.slideDirection || "left"}
                        timeout={{ enter: 350, exit: 0 }}
                    >
                        <Box
                            sx={{
                                minHeight: 0,
                                flexGrow: 1,
                                overflow: "auto",
                                display: "flex",
                                flexDirection: "column"
                            }}>
                            <Renderer closeAiMentorDialog={closeAiMentorDialog} />
                        </Box>
                    </Slide>
                </Fade>
            </TransitionGroup>
        </Box>
    );

} )

export default LearnModeViewRenderer